import React, { useState } from 'react';
import { Loader2, Sparkles, RotateCcw, ExternalLink } from 'lucide-react';
import { analyzeUserNeeds, ConsultantResponse } from '../../services/aiConsultant';
import { connectedApps, categories } from '../../config/categories';

interface GrantConsultantProps {
  onCategorySelect?: (category: string) => void;
}

export const GrantConsultant: React.FC<GrantConsultantProps> = ({ onCategorySelect }) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ConsultantResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    setIsLoading(true);
    try {
      const response = await analyzeUserNeeds(input);
      setResult(response);
    } catch (error) {
      console.error('AI Service Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!result) return;

    // Find recommended app
    const recommendedApp = result.recommendedApp
      ? connectedApps.find((app) => app.id === result.recommendedApp)
      : connectedApps.find((app) => app.category === result.category);

    if (recommendedApp) {
      window.open(recommendedApp.url, '_blank');
    } else if (onCategorySelect) {
      onCategorySelect(result.category);
    }
  };

  const getCategoryInfo = () => {
    if (!result) return null;
    return categories.find((cat) => cat.id === result.category);
  };

  const getRecommendedApp = () => {
    if (!result) return null;
    return result.recommendedApp
      ? connectedApps.find((app) => app.id === result.recommendedApp)
      : connectedApps.find((app) => app.category === result.category);
  };

  const categoryInfo = getCategoryInfo();
  const recommendedApp = getRecommendedApp();

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-maple-600 to-maple-700 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-6 h-6" />
          <h2 className="text-2xl font-bold">Not sure where to start?</h2>
        </div>
        <p className="opacity-90 text-base">
          Tell us about your situation, and our AI Grant Consultant will point you to the right
          funding tool.
        </p>
      </div>

      {/* Content */}
      <div className="p-6">
        {!result ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="story" className="block text-sm font-medium text-slate-700 mb-2">
                Describe your funding needs:
              </label>
              <textarea
                id="story"
                className="w-full p-4 border border-slate-300 rounded-xl min-h-[140px] text-base focus:ring-2 focus:ring-maple-500 focus:border-maple-500 transition-all resize-none"
                placeholder="e.g., I need help paying for a wheelchair ramp for my home in Ontario..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-sm text-slate-500 mt-2">
                Include details like: province, type of support needed, your situation
              </p>
            </div>
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="w-full py-4 px-6 bg-gradient-to-r from-maple-600 to-maple-700 text-white rounded-xl font-semibold text-lg hover:from-maple-700 hover:to-maple-800 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-maple-200"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" size={22} />
                  Analyzing your needs...
                </>
              ) : (
                <>
                  <Sparkles size={22} />
                  Find My Grants
                </>
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-5">
            {/* Result Card */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200">
              <div className="flex items-start gap-3 mb-3">
                <span className="text-3xl">{categoryInfo?.icon || '✨'}</span>
                <div>
                  <h3 className="font-bold text-green-800 text-lg">
                    Recommendation: {categoryInfo?.name}
                  </h3>
                  <p className="text-slate-700 mt-1">{result.summary}</p>
                </div>
              </div>
            </div>

            {/* Recommended App */}
            {recommendedApp && (
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-3">
                  Recommended Tool
                </h4>
                <div className="flex items-start gap-4">
                  <span className="text-4xl">{recommendedApp.icon}</span>
                  <div className="flex-1">
                    <h5 className="font-bold text-slate-800 text-lg">{recommendedApp.name}</h5>
                    <p className="text-slate-600 text-sm mt-1">{recommendedApp.description}</p>
                    <ul className="mt-3 space-y-1">
                      {recommendedApp.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-maple-500 rounded-full" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setResult(null);
                  setInput('');
                }}
                className="flex-1 py-3 px-4 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
              >
                <RotateCcw size={18} />
                Try Again
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-maple-600 to-maple-700 text-white rounded-xl font-semibold hover:from-maple-700 hover:to-maple-800 transition-all flex items-center justify-center gap-2 shadow-lg shadow-maple-200"
              >
                Open Tool
                <ExternalLink size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
